import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ChevronRight, Users, RefreshCw, AlertCircle } from "lucide-react";
import { groundOpsApi } from "@/api";

const DEPT_LABEL = d => d.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

function initials(name) {
  return name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

export default function RampShiftBriefing({ user }) {
  const navigate    = useNavigate();
  const [staff, setStaff]     = useState([]);
  // assignments: { [staffId]: { dept, taskIds: [] } }
  const [assignments, setAssignments] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(`shift_assignments_${user.station}`) || "{}"); } catch { return {}; }
  });
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    groundOpsApi.getStaff(user.station)
      .then(setStaff)
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(load, [user.station]);

  // Merge saved assignments when returning from duty assignment
  function refreshAssignments() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(`shift_assignments_${user.station}`) || "{}");
      setAssignments(saved);
    } catch {}
  }
  useEffect(() => {
    window.addEventListener("focus", refreshAssignments);
    return () => window.removeEventListener("focus", refreshAssignments);
  }, [user.station]);

  const assigned   = staff.filter(s => assignments[s.id]?.taskIds?.length > 0);
  const unassigned = staff.filter(s => !assignments[s.id]?.taskIds?.length);
  const pct        = staff.length ? Math.round(assigned.length / staff.length * 100) : 0;

  return (
    <div className="p-4 max-w-sm mx-auto">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-base font-black text-white">Shift Briefing</h2>
        <p className="text-[11px] text-slate-500">{user.station} · {new Date().toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p>
      </div>

      {/* Progress */}
      <div className="bg-[#0c1929] rounded-2xl border border-slate-700/30 p-4 mb-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Duty Allocation Progress</div>
          <button onClick={() => { load(); refreshAssignments(); }} className="text-slate-600 hover:text-orange-400 transition">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="flex items-end gap-3 mb-3">
          <span className="text-3xl font-black text-white">{assigned.length}</span>
          <span className="text-slate-600 text-sm mb-1">/ {staff.length} staff assigned</span>
        </div>
        <div className="bg-slate-700/30 rounded-full h-2">
          <div
            className={`h-full rounded-full transition-all duration-500 ${pct === 100 ? "bg-green-500" : pct > 50 ? "bg-amber-500" : "bg-orange-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {pct === 100 && (
          <div className="flex items-center gap-1.5 text-[11px] text-green-400 font-bold mt-2">
            <CheckCircle size={12} /> All staff assigned — shift ready
          </div>
        )}
        {pct < 100 && unassigned.length > 0 && (
          <div className="text-[10px] text-amber-400 mt-2">{unassigned.length} staff still need duty allocation</div>
        )}
      </div>

      {loading && <div className="text-center py-8 text-slate-600 animate-pulse text-sm">Loading shift roster...</div>}

      {/* Unassigned staff — assign these first */}
      {!loading && unassigned.length > 0 && (
        <div className="mb-4">
          <div className="text-[9px] text-amber-400 uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5">
            <AlertCircle size={10} /> Needs Assignment ({unassigned.length})
          </div>
          <div className="space-y-2">
            {unassigned.map(s => (
              <button
                key={s.id}
                onClick={() => navigate(`/ramp/assign-duties/${s.id}`, { state: { staffMember: s } })}
                className="w-full flex items-center gap-3 p-3.5 bg-[#0c1929] border border-amber-500/20 hover:border-orange-500/40 rounded-2xl transition-all group"
                data-testid={`assign-staff-${s.id}`}
              >
                <div className="w-10 h-10 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0 text-[11px] font-black text-amber-400">
                  {initials(s.name)}
                </div>
                <div className="flex-1 text-left">
                  <div className="font-bold text-sm text-white">{s.name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{DEPT_LABEL(s.role)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[9px] text-amber-400 font-bold uppercase border border-amber-500/30 bg-amber-500/10 px-2 py-1 rounded-full">
                    Assign Duties
                  </span>
                  <ChevronRight size={16} className="text-slate-600 group-hover:text-orange-400 transition" />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Assigned staff */}
      {!loading && assigned.length > 0 && (
        <div>
          <div className="text-[9px] text-green-400 uppercase tracking-widest font-bold mb-2 flex items-center gap-1.5">
            <CheckCircle size={10} /> Assigned ({assigned.length})
          </div>
          <div className="space-y-2">
            {assigned.map(s => {
              const a = assignments[s.id];
              return (
                <button
                  key={s.id}
                  onClick={() => navigate(`/ramp/assign-duties/${s.id}`, { state: { staffMember: s } })}
                  className="w-full flex items-center gap-3 p-3.5 bg-[#0c1929] border border-green-500/20 hover:border-orange-500/30 rounded-2xl transition-all group"
                  data-testid={`reassign-staff-${s.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0 text-[11px] font-black text-green-400">
                    {initials(s.name)}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-bold text-sm text-white">{s.name}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {DEPT_LABEL(a.dept || s.role)} · {a.taskIds?.length} task{a.taskIds?.length !== 1 ? "s" : ""} assigned
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-green-400" />
                    <ChevronRight size={16} className="text-slate-600 group-hover:text-orange-400 transition" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!loading && staff.length === 0 && (
        <div className="text-center py-12 text-slate-600">
          <Users size={40} className="mx-auto mb-3 opacity-20" />
          <p className="text-sm">No staff found at {user.station}</p>
        </div>
      )}
    </div>
  );
}
